export type PauseMenuCallbacks = {
  onResume: () => void;
  onVolumeChange: (volume: number) => void;
  onSensitivityChange: (sensitivity: number) => void;
  onRenderDistanceChange?: (chunks: number) => void;
  initialRenderDistance?: number;
};

export type PauseMenu = {
  show: () => void;
  hide: () => void;
  isVisible: boolean;
  dispose: () => void;
};

export function createPauseMenu(callbacks: PauseMenuCallbacks): PauseMenu {
  let isVisible = false;

  const overlay = document.createElement("div");
  overlay.id = "pause-menu";
  overlay.style.cssText = `
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.75);
    backdrop-filter: blur(4px);
    z-index: 150;
    display: none;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-family: 'Segoe UI', system-ui, sans-serif;
    color: #e0e0e0;
    gap: 12px;
  `;

  // Title
  const title = document.createElement("h2");
  title.textContent = "PAUSADO";
  title.style.cssText = `
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 20px;
    color: #fff;
    letter-spacing: 4px;
  `;
  overlay.appendChild(title);

  // Resume button
  const resumeBtn = createButton("CONTINUAR", "#4ade80", () => {
    hide();
    callbacks.onResume();
  });
  overlay.appendChild(resumeBtn);

  // Settings section
  const settingsDiv = document.createElement("div");
  settingsDiv.style.cssText = `
    margin-top: 20px;
    padding: 20px;
    background: #1a1a2e;
    border-radius: 12px;
    border: 1px solid #333;
    width: 300px;
  `;

  // Volume slider
  const volumeLabel = document.createElement("label");
  volumeLabel.textContent = "Volume da música";
  volumeLabel.style.cssText = `display:block;font-size:0.85rem;color:#aaa;margin-bottom:6px;`;
  settingsDiv.appendChild(volumeLabel);

  const volumeSlider = document.createElement("input");
  volumeSlider.type = "range";
  volumeSlider.min = "0";
  volumeSlider.max = "100";
  volumeSlider.value = "30";
  volumeSlider.style.cssText = `width:100%;margin-bottom:16px;accent-color:#4ade80;`;
  volumeSlider.addEventListener("input", () => {
    callbacks.onVolumeChange(parseInt(volumeSlider.value) / 100);
  });
  settingsDiv.appendChild(volumeSlider);

  // Sensitivity slider
  const sensLabel = document.createElement("label");
  sensLabel.textContent = "Sensibilidade do mouse";
  sensLabel.style.cssText = `display:block;font-size:0.85rem;color:#aaa;margin-bottom:6px;`;
  settingsDiv.appendChild(sensLabel);

  const sensSlider = document.createElement("input");
  sensSlider.type = "range";
  sensSlider.min = "10";
  sensSlider.max = "100";
  sensSlider.value = "50";
  sensSlider.style.cssText = `width:100%;margin-bottom:16px;accent-color:#4ade80;`;
  sensSlider.addEventListener("input", () => {
    callbacks.onSensitivityChange(parseInt(sensSlider.value) / 100);
  });
  settingsDiv.appendChild(sensSlider);

  // Render distance slider
  const rdLabel = document.createElement("label");
  const initialRd = callbacks.initialRenderDistance ?? 8;
  rdLabel.textContent = `Distância de renderização: ${initialRd} chunks`;
  rdLabel.style.cssText = `display:block;font-size:0.85rem;color:#aaa;margin-bottom:6px;`;
  settingsDiv.appendChild(rdLabel);

  const rdSlider = document.createElement("input");
  rdSlider.type = "range";
  rdSlider.min = "4";
  rdSlider.max = "10";
  rdSlider.step = "1";
  rdSlider.value = String(initialRd);
  rdSlider.style.cssText = `width:100%;accent-color:#4ade80;`;
  rdSlider.addEventListener("input", () => {
    const chunks = parseInt(rdSlider.value);
    rdLabel.textContent = `Distância de renderização: ${chunks} chunks`;
    callbacks.onRenderDistanceChange?.(chunks);
  });
  settingsDiv.appendChild(rdSlider);

  overlay.appendChild(settingsDiv);

  // Controls info
  const controls = document.createElement("div");
  controls.style.cssText = `
    margin-top: 16px;
    font-size: 0.7rem;
    color: #555;
    text-align: center;
    line-height: 1.6;
  `;
  controls.innerHTML = `
    <strong style="color:#888;">Controles</strong><br>
    WASD — Mover · Espaço — Pular · 2×W/Shift — Correr<br>
    1-2-3-4 — Poderes · Scroll/5-9 — Hotbar · Q — Missões<br>
    Clique esq. — Quebrar/Atacar · Clique dir. — Colocar/Comer/Usar<br>
    F5 — Câmera · E — Inventário · Enter — Chat · ESC — Pausa
  `;
  overlay.appendChild(controls);

  document.body.appendChild(overlay);

  function show() {
    isVisible = true;
    overlay.style.display = "flex";
  }

  function hide() {
    isVisible = false;
    overlay.style.display = "none";
  }

  function dispose() {
    overlay.remove();
  }

  return {
    show,
    hide,
    get isVisible() { return isVisible; },
    dispose,
  };
}

function createButton(text: string, color: string, onClick: () => void): HTMLButtonElement {
  const btn = document.createElement("button");
  btn.textContent = text;
  btn.style.cssText = `
    padding: 12px 40px;
    font-size: 1rem;
    font-weight: 600;
    border: 2px solid ${color};
    border-radius: 8px;
    background: transparent;
    color: ${color};
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 1px;
  `;
  btn.addEventListener("mouseenter", () => {
    btn.style.background = `${color}22`;
    btn.style.transform = "scale(1.03)";
  });
  btn.addEventListener("mouseleave", () => {
    btn.style.background = "transparent";
    btn.style.transform = "scale(1)";
  });
  btn.addEventListener("click", onClick);
  return btn;
}
